import React from 'react';
import { CopyableOrderId } from '@/shared/components/CopyableOrderId';
import { Order } from '@/shared/types';
import { formatPhotoUrl } from '@/shared/utils/formatter';
import './styles/OrderCard.scss';

interface OrderCardProps {
  order: Order;
  onClick: () => void;
  onPay?: (orderId: string) => void;
  paying?: boolean;
}

const statusColors = {
  pending: { bg: '#fff3e0', color: '#e65100', text: 'Ожидает оплаты' },
  processing: { bg: '#e3f2fd', color: '#0d47a1', text: 'Готовится' },
  completed: { bg: '#e8f5e8', color: '#1b5e20', text: 'Оплачен' },
  cancelled: { bg: '#ffebee', color: '#b71c1c', text: 'Отменён' },
};

const NO_IMAGE = '/icons/no-image.svg';

export const OrderCard: React.FC<OrderCardProps> = ({
  order,
  onClick,
  onPay,
  paying = false,
}) => {
  const status = statusColors[order.status];
  const date = new Date(order.date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  const previewItems = order.items.slice(0, 2);
  const remainingCount = order.items.length - previewItems.length;

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = NO_IMAGE;
  };
  const canShowPayButton = Boolean(order.canPayOnline && order.editable && onPay);

  return (
    <div className="order-card" onClick={onClick}>
      <div className="order-card__top">
        <span
          className="order-card__status"
          style={{
            backgroundColor: status.bg,
            color: status.color,
          }}
        >
          {status.text}
        </span>
        <span className="order-card__date">{date}</span>
      </div>

      <CopyableOrderId
        orderId={order.idZakaz || order.id}
        className="order-card__order-id"
      />

      <div className="order-card__lines">
        {previewItems.map((item, index) => (
          <div key={index} className="order-card__line">
            <div className="order-card__thumb">
              <img
                src={formatPhotoUrl(item.product.photo)}
                alt={item.product.name}
                onError={handleImageError}
              />
            </div>
            <div className="order-card__line-info">
              <span className="order-card__line-name">{item.product.name}</span>
              <span className="order-card__line-qty">×{item.quantity}</span>
            </div>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="order-card__more">
            +{remainingCount}{' '}
            {remainingCount === 1 ? 'товар' : 'товара'}
          </div>
        )}
      </div>

      <div className="order-card__footer">
        <span className="order-card__total-label">Итого:</span>
        <span className="order-card__total-amount">
          {order.totalAmount.toLocaleString('ru-RU')} ₽
        </span>
      </div>
      {canShowPayButton && (
        <button
          type="button"
          className="order-card__pay-btn"
          onClick={(e) => {
            e.stopPropagation();
            onPay?.(order.id);
          }}
          disabled={paying}
        >
          {paying ? 'Подготовка оплаты...' : 'Оплатить заказ'}
        </button>
      )}
    </div>
  );
};
